import * as React from 'react'
import StageHeader from '../../shared/StageHeader'
import FormLayout from '../../shared/FormLayout'
import { Divider } from '../../shared/Divider'
import { FieldsetSelect } from '@/modules/settings/components/FieldsetSelect'
import useContractFieldSetOptions from '../options/useContractFieldSetOptions'
import { useContractForm } from '@/modules/drivers/context/ContractFormContext'
import CheckboxWithLabelField from '../inputs/CheckboxWithLabelField'
import LabelHeader from '../components/LabelHeader'
import Radio from '@/modules/drivers/shared/Radio'
import TaxFree from '../components/wages/TaxFree'
import PkkMembership from '../components/wages/PkkMembership'
import TaxesInclude from '../components/wages/TaxesInclude'
import { NettoRateType } from '@/app/constants/nettoRateType'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { getCivilLawCostsOfIncomeTypeName } from '@/app/common/converters/contract/civilLawCostsOfIncomeTypeToTextConverter'
import NettoOptions from '../components/shared/NettoOptions'

const ContractWages = () => {
  const { contractNettoOptions } = useContractFieldSetOptions()

  const { t } = useLocalize()
  const { setData, data } = useContractForm()
  const [isStudent, setIsStudent] = React.useState(false)
  const WagesForCivilLawAgreement = data?.type === 'CivilLawAgreement'

  const Basic =
    data?.type === 'Basic' ||
    data?.type === 'Equivalent' ||
    data?.type === 'Intermittent' ||
    data?.type === 'Mix' ||
    data?.type === 'Task'

  const isSelfEmployed = data?.type === 'SelfEmployment'

  const checkboxesForWages = [
    {
      field: 'includeTaxFreeAmount',
      label: t('contract.include-tax-free-amount-pit2'),
      children: <TaxFree setData={setData} data={data} />,
      visible: WagesForCivilLawAgreement || Basic,
    },
    {
      field: 'isTaxExempt',
      label: t('contract.pit-exemption-under-26'),
      visible: WagesForCivilLawAgreement || Basic,
    },
    {
      field: 'isFamilyFourPlusRelief',
      label: t('contract.family-4plus-relief'),
      visible: WagesForCivilLawAgreement || Basic,
    },
    {
      field: 'isSeniorRelief',
      label: t('contract.senior-relief'),
      visible: WagesForCivilLawAgreement || Basic,
    },
    {
      value: isStudent,
      field: 'reduceRateOfAbsenceForNotPaidLeave',
      label: t('contract.student-no-zus'),
      visible: WagesForCivilLawAgreement,
      onChange: (checked) => setIsStudent(checked),
      disabled: !data?.isTaxExempt,
    },
    {
      field: 'includeSocialInsurance',
      label: t('contract.pension-disability-contributions'),
      defaultChecked: !isStudent && WagesForCivilLawAgreement,
      disabled: isStudent,
      visible: WagesForCivilLawAgreement,
    },
    {
      field: 'includeSickInsurance',
      label: t('contract.sickness-contribution'),
      disabled: isStudent,
      visible: WagesForCivilLawAgreement,
    },
    {
      field: 'isSecondThresholdTaxEnabled',
      label: t('contract.include-tax-thresholds'),
      children: <TaxesInclude setData={setData} data={data} />,
      defaultChecked: true,
      visible: Basic || WagesForCivilLawAgreement,
    },
    {
      field: 'isPpkEnabled',
      label: t('contract.ppk-participation'),
      children: <PkkMembership setData={setData} data={data} />,
      visible: Basic || WagesForCivilLawAgreement,
    },
   
  ]

  React.useEffect(() => {
    if (isStudent) {
      setData((prev) => ({
        ...prev,
        includeSocialInsurance: false,
        includeSickInsurance: false,
      }))
    }
  }, [isStudent])

  React.useEffect(() => {
    setData((prev) => {
      if (!prev) {
        return prev
      }

      const next = {
        ...prev,
        homelandTaxResidence: true,
      }

      if (Basic && next.increasedTaxCosts == null) {
        next.increasedTaxCosts = 0
      }

      if (
        WagesForCivilLawAgreement &&
        next.increasedCivilLawCostsOfIncome == null
      ) {
        next.increasedCivilLawCostsOfIncome = 0
      }

      return next
    })
  }, [Basic, WagesForCivilLawAgreement, setData])

  return (
    <div className="tw-flex tw-min-h-[500px] tw-justify-between">
      <StageHeader title={t('contract.contract-parameters')} />
      <FormLayout>
        {!isSelfEmployed && (
          <>
            <CheckboxWithLabelField
              field="homelandTaxResidence"
              data={data}
              defaultChecked={true}
              setData={setData}
              label={t('contract.tax-residence-pl')}
            />
            <Divider />
          </>
        )}
        <div>
          <FieldsetSelect
            required
            label={t('contract.rate-type')}
            placeholder={t('common.select')}
            value={data?.nettoType?.toString() ?? ''}
            name="type"
            onChange={(e) => {
              setData({
                ...data,
                nettoType: Number(e.target.value) as NettoRateType,
              })
            }}
            options={contractNettoOptions}
          />
          {data?.nettoType !== undefined && (
            <NettoOptions data={data} setData={setData} />
          )}
        </div>

        {!isSelfEmployed && (
          <>
            <LabelHeader label={t('contract.cost-of-income')} />
            {WagesForCivilLawAgreement ? (
              <>
                <div className="tw-flex tw-flex-col tw-items-start tw-gap-3">
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Radio
                      checked={data?.increasedCivilLawCostsOfIncome === 2}
                      onChange={(e) =>
                        setData({ ...data, increasedCivilLawCostsOfIncome: 2 })
                      }
                    />
                    <p className="tw-text-sm tw-text-content-base">
                      {getCivilLawCostsOfIncomeTypeName(2, t)}
                    </p>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Radio
                      checked={data?.increasedCivilLawCostsOfIncome === 0}
                      onChange={(e) =>
                        setData({ ...data, increasedCivilLawCostsOfIncome: 0 })
                      }
                    />
                    <p className="tw-text-sm tw-text-content-base">
                      KUP {getCivilLawCostsOfIncomeTypeName(0, t)}
                    </p>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Radio
                      checked={data?.increasedCivilLawCostsOfIncome === 1}
                      onChange={(e) =>
                        setData({ ...data, increasedCivilLawCostsOfIncome: 1 })
                      }
                    />
                    <p className="tw-text-sm tw-text-content-base">
                      KUP {getCivilLawCostsOfIncomeTypeName(1, t)}
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="tw-flex tw-flex-col tw-items-start tw-gap-3">
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Radio
                      checked={data?.increasedTaxCosts === 2}
                      onChange={(e) =>
                        setData({ ...data, increasedTaxCosts: 2 })
                      }
                    />
                    <p className="tw-text-sm tw-text-content-base">
                      {t('common.none')}
                      {/* {getTaxCostsTypeName(2, t)} */}
                    </p>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Radio
                      checked={data?.increasedTaxCosts === 0}
                      onChange={(e) =>
                        setData({ ...data, increasedTaxCosts: 0 })
                      }
                    />
                    <p className="tw-text-sm tw-text-content-base">
                      KUP-250
                      {/* {getTaxCostsTypeName(0, t)} */}
                    </p>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Radio
                      checked={data?.increasedTaxCosts === 1}
                      onChange={(e) =>
                        setData({ ...data, increasedTaxCosts: 1 })
                      }
                    />
                    <p className="tw-text-sm tw-text-content-base">
                      KUP-300.
                      {/* {getTaxCostsTypeName(1, t)} */}
                    </p>
                  </div>
                </div>
              </>
            )}

            <Divider />
            <LabelHeader label={t('contract.options')} />
            <div>
              {checkboxesForWages.map((c, i) => (
                <CheckboxWithLabelField
                  key={i}
                  value={c.value}
                  defaultChecked={c.defaultChecked}
                  field={c.field as keyof typeof data}
                  data={data}
                  onChange={c.onChange}
                  setData={setData}
                  disabled={c.disabled}
                  label={c.label}
                >
                  {c.children}
                </CheckboxWithLabelField>
              ))}
            </div>
          </>
        )}
      </FormLayout>
    </div>
  )
}

export default ContractWages
